import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import {
  deleteExplorerView,
  listExplorerViews,
  saveExplorerView,
  toggleExplorerViewPinned,
} from "../services/savedViewsService";

/**
 * Saved Views for the Collection Explorer — captures collection, filters,
 * columns, sort, density, page size and search so a full working view can be
 * restored in one click.
 */
export default function ExplorerSavedViewsDialog({ open, onClose, collectionName, currentState, onApply }) {
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");

  const refresh = () => setViews(listExplorerViews(collectionName));

  useEffect(() => {
    if (open) { refresh(); setName(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collectionName]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    saveExplorerView({ name: trimmed, collection: collectionName, state: currentState });
    setName("");
    refresh();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Saved Views</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Save current view as…"
            placeholder="Today's Attendance, Late Employees, Main Branch…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>Save</Button>
        </Stack>

        <Divider />

        {views.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 2 }}>No saved views yet.</Typography>
        ) : (
          <List dense>
            {views.map((view) => {
              const filterCount = view.state?.filters?.length || 0;
              return (
                <ListItem
                  key={view.id}
                  disablePadding
                  secondaryAction={
                    <Box>
                      <IconButton size="small" onClick={() => { toggleExplorerViewPinned(view.id); refresh(); }}>
                        {view.pinned ? <PushPinIcon fontSize="small" color="primary" /> : <PushPinOutlinedIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => { deleteExplorerView(view.id); refresh(); }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => { onApply(view); onClose(); }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{view.name}</span>
                          {view.collection && <Chip size="small" variant="outlined" label={view.collection} />}
                        </Stack>
                      }
                      secondary={`${filterCount} filter(s) · ${view.state?.pageSize || 100}/page · ${view.state?.density || "standard"}`}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
